import { FilmoscopeApi } from '@kinopoisk-int/filmoscope';
import {
    FilmoscopeAdapter,
    HTMLRequestSender,
    PlayerTrackerWithPluggableSender,
    flattenTrackingEvents,
    parseTrackings,
} from '@kinopoisk-int/ott-player-tracker';
import type { TrackingEvents } from '@kinopoisk-int/ott-player-tracker/dist/lib/PlayerTracker/types';
import buildTrackings, {
    getConfig as getTrackingsBuilderConfig,
} from '@kinopoisk-int/ott-trackings-builder';
import type { NewPlayerTrackerEventsConfig } from '@kinopoisk-int/ott-player-tracker/dist/lib/PlayerTrackerWithPluggableSender/types';
import { Environment } from '@kinopoisk-int/kp-environment';
import type { TrackingData } from '@kinopoisk-int/ott-trackings-builder/lib/types';

import { clientConfig } from '@/config/client';

export type PlayerTrackerData = Partial<TrackingData>;

// ts-prune-ignore-next
export type StreamTrackingData = Partial<{
    serviceName: string;
    contentTypeId: number;
    sid: string;
    uuid: string;
    kpId: number;
    monetizationModel: string;
    puid: number;
    contentPackage: string;
    billingFeatureNames: string;
    vitrinaTvChannelSlug: string;
}>;

const { trackingsHost, environment } = clientConfig;

interface TrackingsWithPreroll {
    trackingEvents: TrackingEvents;
    prerollDuration: number;
}

interface InitTrackerInstanceParams {
    trackings: TrackingsWithPreroll;
    tracker: PlayerTrackerWithPluggableSender<FilmoscopeAdapter>;
    sender: HTMLRequestSender;
}

function uniqueByValue<T>(items: T[]): T[] {
    const seen = new Set<string>();
    return items.filter((item) => {
        const key = JSON.stringify(item);
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
    });
}

function getTrackingsConfig(context: PlayerTrackerData) {
    const [backends, metrikaCounters] = getTrackingsBuilderConfig({
        env:
            environment && [Environment.Production, Environment.Prestable].includes(environment)
                ? 'production'
                : 'testing',
    });

    const trackings = buildTrackings(
        context,
        { ...backends, ottTrackingsBackend: `${trackingsHost}/` },
        metrikaCounters
    );

    return trackings;
}

function buildTrackingsPayload(
    trackerData: PlayerTrackerData
): InitTrackerInstanceParams['trackings'] {
    const tracking = getTrackingsConfig(trackerData);

    return {
        trackingEvents: flattenTrackingEvents(parseTrackings(tracking).trackingEvents),
        prerollDuration: 0,
    };
}

interface PlayerTrackerManager {
    init(filmoscope: FilmoscopeApi): void;
    setTrackings(trackerData: PlayerTrackerData, contentId: string): void;
}

export function createPlayerTrackerManager(): PlayerTrackerManager {
    const sender = new HTMLRequestSender({
        debug: document.cookie.includes('_kp_debug='),
        getIsSendBeaconAllowed: () => true,
    });

    let tracker: PlayerTrackerWithPluggableSender<FilmoscopeAdapter> | undefined;
    let lastContentId: string | undefined;

    return {
        init(filmoscope) {
            const player = new FilmoscopeAdapter(filmoscope);
            tracker = new PlayerTrackerWithPluggableSender({
                sender,
                player,
                adController: null,
            });
            lastContentId = undefined;
        },

        setTrackings(trackerData, contentId) {
            if (!tracker || trackerData.uuid !== contentId || lastContentId === contentId) {
                return;
            }

            lastContentId = contentId;

            const trackings = buildTrackingsPayload(trackerData);

            const conf: NewPlayerTrackerEventsConfig = {
                heartbeat: uniqueByValue(trackings.trackingEvents.heartbeat.map((hb) => hb[1])),
                viewed: uniqueByValue(trackings.trackingEvents.viewed.map((v) => v[1])),
                watchedTime: uniqueByValue(trackings.trackingEvents.watchedTime.map((wt) => wt[1])),
                prerollDuration: trackings.prerollDuration,
            };

            tracker.setTrackings(conf);
            sender.setTrackings(trackings);
        },
    };
}
